// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/state-machines/typography-machine.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/state-machines/src/typography-machine.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo cash-sync-app
//     node fleet/fleet.ts check --repo cash-sync-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { assign, fromPromise, setup } from "xstate";
import type { FontFamilyWithVariants, FontWeight } from "@fleet/typography";

export interface FontDownloaderVariant {
  weight: FontWeight;
  url?: string;
  attachment_id?: string;
  id?: string;
  font_family_id?: string | null;
}

export interface IFontDownloaderService {
  downloadAndLoad(
    familyName: string,
    variants: readonly FontDownloaderVariant[],
    db: unknown,
  ): Promise<void>;
  clearFamilyKeys(familyName: string): void;
  isLoaded?(fontKey: string): boolean;
  getDiskCacheStatus?(
    family: string,
    variants: readonly FontDownloaderVariant[],
  ): Promise<"cached" | "partial" | "not-cached" | "not-applicable">;
}

export interface TypographyMachineContext {
  activeFamilyId: string | null;
  activeFamilyName: string | null;
  activeVariantMap: Record<string, string>;
  loadedRev: number;
  lastFontDataStr: string | null;
  downloader: IFontDownloaderService;
  getFontKey: (family: string, weight: FontWeight) => string;
}

export type TypographyEvent = {
  type: "SYNC_DATA";
  fontFamilyId: string | null;
  fontData: FontFamilyWithVariants | null;
  db: unknown;
};

export interface TypographyMachineInput {
  downloader: IFontDownloaderService;
  getFontKey: (family: string, weight: FontWeight) => string;
}

const RADIX_DECIMAL = 10;
const FONT_WEIGHTS = ["100", "200", "300", "400", "500", "600", "700", "800", "900"] as const;

function getClosestWeight(requested: FontWeight, available: FontWeight[]): FontWeight {
  if (available.includes(requested)) {
    return requested;
  }
  const reqNum = Number.parseInt(requested, RADIX_DECIMAL);
  let closest = available[0] ?? "400";
  let minDiff = Math.abs(Number.parseInt(closest, RADIX_DECIMAL) - reqNum);
  for (const w of available) {
    const diff = Math.abs(Number.parseInt(w, RADIX_DECIMAL) - reqNum);
    if (diff < minDiff) {
      minDiff = diff;
      closest = w;
    }
  }
  return closest;
}

const downloadFonts = fromPromise<
  {
    familyName: string;
    variantMap: Record<string, string>;
    fontFamilyId: string | null;
    fontDataStr: string | null;
  },
  { input: TypographyEvent; config: TypographyMachineInput }
>(async ({ input }) => {
  const { fontData, db, fontFamilyId } = input.input;
  const { downloader, getFontKey } = input.config;
  const fontDataStr = fontData ? JSON.stringify(fontData) : null;

  if (!fontData || !db) {
    return { familyName: "", variantMap: {}, fontFamilyId, fontDataStr };
  }

  const familyName = fontData.name ?? fontData.id;
  const { variants } = fontData;
  const validVariants = variants.filter((v) => Boolean(v.weight && (v.url ?? v.attachment_id)));

  if (validVariants.length === 0) {
    return { familyName, variantMap: {}, fontFamilyId, fontDataStr };
  }

  const downloaderVariants = validVariants
    .filter(
      (v): v is typeof v & { weight: FontWeight } =>
        typeof v.weight === "string" && (FONT_WEIGHTS as readonly string[]).includes(v.weight),
    )
    .map((v) => ({
      weight: v.weight,
      url: v.url ?? "",
      attachment_id: v.attachment_id ?? undefined,
    }));

  downloader.clearFamilyKeys(familyName);
  try {
    await downloader.downloadAndLoad(familyName, downloaderVariants, db);
  } catch (error) {
    console.warn(`[Typography] Error loading font ${familyName}:`, error);
  }

  const availableWeights = downloaderVariants
    .map((v) => v.weight)
    .toSorted((a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10));
  const variantMap = {
    "-Regular": getFontKey(familyName, getClosestWeight("400", availableWeights)),
    "-Medium": getFontKey(familyName, getClosestWeight("500", availableWeights)),
    "-SemiBold": getFontKey(familyName, getClosestWeight("600", availableWeights)),
    "-Bold": getFontKey(familyName, getClosestWeight("700", availableWeights)),
  };

  return { familyName, variantMap, fontFamilyId, fontDataStr };
});

export const typographyMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: TypographyMachineContext;
    events: TypographyEvent;
    input: TypographyMachineInput;
  },
  actors: {
    downloadFonts,
  },
}).createMachine({
  id: "typography",
  initial: "idle",
  context: ({ input }) => ({
    activeFamilyId: null,
    activeFamilyName: null,
    activeVariantMap: {},
    loadedRev: 0,
    lastFontDataStr: null,
    downloader: input.downloader,
    getFontKey: input.getFontKey,
  }),
  states: {
    idle: {
      on: { SYNC_DATA: "evaluating" },
    },
    evaluating: {
      always: [
        {
          guard: ({ event }) => !event.fontFamilyId,
          target: "ready",
          actions: assign({
            activeFamilyId: null,
            activeFamilyName: null,
            activeVariantMap: {},
            lastFontDataStr: null,
            loadedRev: ({ context }) => context.loadedRev + 1,
          }),
        },
        {
          guard: ({ event }) => Boolean(event.fontFamilyId) && !event.fontData,
          target: "waitingForData",
        },
        {
          guard: ({ context, event }) => {
            const currentDataStr = event.fontData ? JSON.stringify(event.fontData) : null;
            return (
              event.fontFamilyId !== context.activeFamilyId ||
              currentDataStr !== context.lastFontDataStr
            );
          },
          target: "downloading",
        },
        { target: "ready" },
      ],
    },
    waitingForData: {
      on: { SYNC_DATA: "evaluating" },
    },
    downloading: {
      on: { SYNC_DATA: "evaluating" },
      invoke: {
        src: "downloadFonts",
        input: ({ context, event }) => ({
          input: event,
          config: {
            downloader: context.downloader,
            getFontKey: context.getFontKey,
          },
        }),
        onDone: {
          target: "ready",
          actions: assign({
            activeFamilyId: ({ event }) => event.output.fontFamilyId,
            activeFamilyName: ({ event }) => event.output.familyName,
            activeVariantMap: ({ event }) => event.output.variantMap,
            lastFontDataStr: ({ event }) => event.output.fontDataStr,
            loadedRev: ({ context }) => context.loadedRev + 1,
          }),
        },
        onError: {
          target: "ready",
          actions: assign({ loadedRev: ({ context }) => context.loadedRev + 1 }),
        },
      },
    },
    ready: {
      on: { SYNC_DATA: "evaluating" },
    },
  },
});
